import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import IconButton from "@mui/joy/IconButton";
import Slider from "@mui/joy/Slider";

import RotateLeftRoundedIcon    from "@mui/icons-material/RotateLeftRounded";
import RotateRightRoundedIcon   from "@mui/icons-material/RotateRightRounded";
import ImageSearchRoundedIcon   from "@mui/icons-material/ImageSearchRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon         from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon         from "@mui/icons-material/CheckRounded";
import AddRounded               from "@mui/icons-material/AddRounded";
import RemoveRounded            from "@mui/icons-material/RemoveRounded";
import { Modal, ModalDialog, ModalOverflow } from "@mui/joy";
import { isFitImageMode, getCompressOn, setCompressOn, compressImage } from "../../fitImage";
import { isConverterMode, getConvertOn, setConvertOn, convertHeicToPng, isHeicFile } from "../../heicConverter";
import { logEvent } from "../../logger";



// SHA-256 (hex) содержимого файла. Пусто при ошибке — сервер тогда посчитает хеш сам.
async function sha256Hex(file) {
  try {
    const buf = await file.arrayBuffer();
    const hashBuf = await crypto.subtle.digest("SHA-256", buf);
    return Array.from(new Uint8Array(hashBuf))
      .map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    return "";
  }
}


// ─── Константы ───────────────────────────────────────────────────────────────

// БАЗОВАЯ доля корешка (8% ширины страницы) — используется как фолбэк, если
// проп spineFraction не передан. В норме доля приходит готовой из
// spine.config.getSpineFraction() и для ячеек макетов она больше базы.
const SPINE_PERCENT = 0.08;
const BLEED_PERCENT = 0.025;

function clamp(val, min, max) { return Math.max(min, Math.min(max, val)); }
// Небольшой запас перекрытия рамки (0.5%): гарантирует, что фото ВСЕГДА чуть
// перекрывает рамку по всем сторонам — убирает тонкую щель от дробных пикселей/округления.
const COVER_EPS = 1.02;   // запас перекрытия рамки: 0.5% не хватало на субпиксельный
                          // рендер (полоски фона + подзум при пружине); 2% надёжно
                          // покрывает при любом зуме страницы. Обрезка по краю ~1%/сторону —
                          // незаметна и попадает в зону обреза.

// Границы зума для слайдера/кнопок. ZOOM_MAX совпадает с потолком колеса/пинча (8).
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.15;   // множитель на один клик «+»/«−»

// ─── Кнопка тулбара ──────────────────────────────────────────────────────────

function ToolBtn({ icon, label, onClick, danger }) {
  return (
    <Box onClick={onClick} sx={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "5px",
      px: 1.5, py: 1.25, borderRadius: "16px", cursor: "pointer",
      WebkitTapHighlightColor: "transparent",
      transition: "transform 0.12s",
      "&:active": { transform: "scale(0.88)" },
    }}>
      <Box sx={{
        width: 52, height: 52, borderRadius: "50%",
        bgcolor: danger ? "rgba(200,40,40,0.18)" : "neutral.200",
        border: danger ? "1.5px solid rgba(255,80,80,0.4)" : "1.5px solid rgba(255,255,255,0.16)",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: danger ? "#ff7070" : "rgba(255,255,255,0.16)",
        "& svg": { fontSize: 22 },
      }}>
        {icon}
      </Box>
      <Typography sx={{
        fontSize: 12, lineHeight: 1.2, textAlign: "center",
        color: danger ? "#ca3131" : "neutral.500",
        fontWeight: 400, maxWidth: 70,
      }}>
        {label}
      </Typography>
    </Box>
  );
}

// ─── Главный компонент ────────────────────────────────────────────────────────

export default function ModalPhotoEditor({
  open, photo, side, bookSize, slotAspectRatio,
  spineFraction, bleedFractionX, bleedFractionY,
  isCoverBack, slotEdges, logContext,
  onConfirm, onDelete, onReplace, onClose,
  coverShape, busy = false, exactCoverAspect = false,
  previewBackground = "#fff",
}) {
    const replaceInputRef = useRef(null);
    const frameRef     = useRef(null);
    const imgRef       = useRef(null);

    const [rotation,   setRotation]   = useState(0);
    const [scale,      setScale]      = useState(1);
    const [offset,     setOffset]     = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [imgNatural, setImgNatural] = useState({ w: 0, h: 0 });
    const [layoutTick, setLayoutTick] = useState(0);   // форс-пересчёт размеров после загрузки/раскладки

    // spine — красный корешок (обрезается)
    // yellow — желтый bleed (НЕ обрезается, но визуально показываем края)
    const [spine, setSpine] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
    const [yellow, setYellow] = useState({ top: 0, bottom: 0, left: 0, right: 0 });

    const dragStart    = useRef(null);
    const offsetStart  = useRef({ x: 0, y: 0 });
    const pinchStart   = useRef(null);
    const containerRef = useRef(null);
    const didConvertRef = useRef(false);   // было ли фото сконвертировано из HEIC (для лога)
    const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });

    // ── URL источника для редактора ──────────────────────────────────────────
    // Всегда редактируем ОРИГИНАЛ, а не кропнутый превью
    const originalSrc = photo?.originalUrl || photo?.url;
    const fitMode = isFitImageMode();   // сжатие доступно только по «умной ссылке»
    const convertMode = isConverterMode();   // конвертер HEIC→PNG — тоже по «умной ссылке»
    const [compress, setCompress]     = useState(fitMode ? getCompressOn() : false);
    const [convert, setConvert]       = useState(convertMode ? getConvertOn() : false);
    const [displaySrc, setDisplaySrc] = useState((fitMode || convertMode) ? "" : originalSrc);
    const [preparing, setPreparing]   = useState(false);
    const [ready, setReady]           = useState(false);   // редактор готов: оригинал загружен и кадр восстановлен
    const readyRef                    = useRef(false);      // мгновенная блокировка жестов (без stale-closure)

    // Реактивная копия minScaleRef для нижней границы слайдера зума: слайдер
    // не должен позволять «отдалить» ниже покрытия — такой кадр физически
    // нельзя сохранить (в печати появились бы поля), и при сохранении он
    // молча доводится до покрывающего, что выглядит как внезапный зум.
    const [minZoom, setMinZoom] = useState(1);

    // Оригинал недоступен (файл удалён/битый) — показываем понятное сообщение,
    // а не битую иконку и «живой», но неработающий редактор.
    const [srcError, setSrcError] = useState(false);

    // editorSrc — что показываем И из чего кропаем при подтверждении.
    const editorSrc = displaySrc;

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        let createdUrl = null;
        didConvertRef.current = false;   // сбрасываем пометку для нового фото
        const needConvert  = convertMode && convert && isHeicFile(photo?.file);
        const needCompress = fitMode && compress && !!photo?.file;
        if (needConvert || needCompress) {
            setPreparing(true);
            setDisplaySrc("");   // прячем чёрный/битый оригинал на время обработки
            (async () => {
                let f = photo.file;
                // 1) HEIC → PNG (конвертер включён). Сбой — понятное сообщение и закрытие,
                //    чтобы на сервер НЕ уходило битое/чёрное фото.
                if (needConvert) {
                    try {
                        f = await convertHeicToPng(f);
                        didConvertRef.current = true;   // фото реально сконвертировано
                    } catch (e) {
                        if (!cancelled) {
                            setPreparing(false);
                            alert("Не удалось обработать это фото (формат HEIC). Попробуйте другое фото или сохраните его как JPEG.");
                            onClose?.();
                        }
                        return;
                    }
                }
                // 2) Сжатие (если включено). Сбой — оставляем как есть.
                if (needCompress) {
                    try { f = await compressImage(f); } catch (e) {}
                }
                if (cancelled) return;
                createdUrl = URL.createObjectURL(f);
                setDisplaySrc(createdUrl);
                setPreparing(false);
            })();
        } else {
            setPreparing(false);
            setDisplaySrc(originalSrc);
        }
        return () => { cancelled = true; if (createdUrl) URL.revokeObjectURL(createdUrl); };
    }, [open, photo?.file, originalSrc, fitMode, compress, convertMode, convert]);

    // ── Инициализация при открытии / смене фото ──────────────────────────────
    useEffect(() => {
        if (open && photo) {
            setIsDragging(false);
            setReady(false);
            readyRef.current = false;
            // Новая версия определяется наличием crop_data.src
            const cd = (photo.cropData && photo.cropData.src) ? photo.cropData : null;

            // Поворот выставляем сразу (нужен для расчёта размеров рамки/фото)
            setRotation(cd ? (((cd.rotation % 360) + 360) % 360) : (photo.rotation ?? 0));
            setScale(cd ? 1 : (photo.scale ?? 1));

            const restore = () => {
                const frame = frameRef.current;
                if (!frame || !imgNatural.w) return;
                const { width: fw, height: fh } = frame.getBoundingClientRect();
                if (fw === 0) return;

                const rot = cd ? (((cd.rotation % 360) + 360) % 360) : (photo.rotation ?? 0);
                const isRot90 = Math.abs(rot % 180) === 90;
                const natW = isRot90 ? imgNatural.h : imgNatural.w;
                const natH = isRot90 ? imgNatural.w : imgNatural.h;
                const imgAr = natW / natH;
                const frameAr = fw / fh;
                let baseW, baseH;
                if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
                else                  { baseW = fw; baseH = fw / imgAr; }

                if (cd) {
                    // НОВАЯ ВЕРСИЯ: инверсия handleConfirm из src, но НАДЁЖНО:
                    // проверяем соответствие рамки + клэмпим зум/смещение.
                    const s = cd.src;
                    const sw = s.w || 1, sh = s.h || 1;
                    const coverMin = Math.max(1, fw / baseW, fh / baseH) * COVER_EPS;

                    // Аспект, под который считался src, должен совпасть с текущей рамкой.
                    const savedAr = (sw * natW) / (sh * natH);
                    const arOk = Math.abs(savedAr / frameAr - 1) <= 0.08;   // ±8%

                    const rW = fw / sw;
                    const rH = fh / sh;
                    const sc = rW / baseW;

                    if (arOk && sc >= coverMin * 0.999 && sc <= ZOOM_MAX) {
                        // Данные валидны и соответствуют рамке → точное восстановление + клэмп смещения.
                        setScale(sc);
                        const imgLeft = -(s.x || 0) * fw / sw;
                        const imgTop  = -(s.y || 0) * fh / sh;
                        const maxX = Math.max(0, (rW - fw) / 2);
                        const maxY = Math.max(0, (rH - fh) / 2);
                        setOffset({
                            x: clamp(imgLeft - (fw - rW) / 2, -maxX, maxX),
                            y: clamp(imgTop  - (fh - rH) / 2, -maxY, maxY),
                        });
                    } else {
                        // Координаты не соответствуют текущей рамке (старый код/битые данные)
                        // или зум вне диапазона → безопасный cover. Никакого «x2».
                        setScale(coverMin);
                        setOffset({ x: 0, y: 0 });
                    }
                } else {
                    // LEGACY / НОВОЕ фото. Если сохранённого масштаба нет — стартуем с
                    // ПОКРЫВАЮЩЕГО минимума (с COVER_EPS), а НЕ с 1: при scale=1 тесная
                    // сторона впритык → сквозь субпиксель виден фон редактора («полоски»),
                    // и при пружине (snapBack) фото само подзумливается до cover. Старт с
                    // cover это убирает. Никогда не ниже покрытия.
                    const coverMin = Math.max(1, fw / baseW, fh / baseH) * COVER_EPS;
                    const sc = Math.max(photo.scale ?? coverMin, coverMin);
                    setScale(sc);
                    setOffset({
                        x: (photo.offsetX ?? 0) * baseW * sc,
                        y: (photo.offsetY ?? 0) * baseH * sc,
                    });
                }
            };

            setOffset({ x: 0, y: 0 });
            // ДЕТЕРМИНИРОВАННО: ждём (через rAF), пока измерится ОРИГИНАЛ (imgNatural)
            // и разложится рамка — ТОЛЬКО потом восстанавливаем кадр и снимаем блокировку.
            // До готовности жесты заблокированы (readyRef), а сверху висит блюр-оверлей.
            let cancelled = false, tries = 0;
            const tick = () => {
                if (cancelled) return;
                const frame = frameRef.current;
                const rectOk = !!(frame && frame.getBoundingClientRect().width > 0);
                if (imgNatural.w && rectOk) {
                    restore();
                    setReady(true);
                    readyRef.current = true;
                    return;
                }
                // Ждём готовности; если время вышло (например, картинка не загрузилась) —
                // всё равно снимаем блокировку, чтобы редактор не завис навсегда.
                if (tries++ < 90) requestAnimationFrame(tick);
                else { setReady(true); readyRef.current = true; }
            };
            requestAnimationFrame(tick);
            return () => { cancelled = true; };
        }
    }, [open, photo?.url, photo?.originalUrl, imgNatural]);


    // Размер доступной области не должен зависеть от масштаба анимации.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        let raf = 0;
        let observer;
        const attach = () => {
            if (cancelled) return;
            const el = containerRef.current;
            if (!el) {
                raf = requestAnimationFrame(attach);
                return;
            }
            const measure = () => {
                if (cancelled) return;
                const w = el.clientWidth;
                const h = el.clientHeight;
                if (w <= 0 || h <= 0) return;
                setContainerSize(prev => prev.w === w && prev.h === h
                    ? prev : { w, h });
            };
            observer = new ResizeObserver(measure);
            observer.observe(el);
            measure();
        };
        attach();
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf);
            observer?.disconnect();
        };
    }, [open, imgNatural.w, imgNatural.h, coverShape]);

    // ── Загружаем натуральные размеры ОРИГИНАЛЬНОГО фото ────────────────────
    // decode() дожидается ПОЛНОГО декодирования (важно для тяжёлых фото). После этого
    // форсируем пересчёт размеров: к этому моменту и фото готово, и рамка разложена —
    // поэтому размеры считаются от финальной рамки, а не от «неготовой» (гонка).
    useEffect(() => {
        if (!editorSrc) return;
        let cancelled = false;
        // Сбрасываем размеры ПРЕДЫДУЩЕГО фото. Иначе, пока новое декодируется,
        // восстановление кадра и подтверждение считают по размерам старого фото —
        // координаты кропа получаются произвольными (сдвиг/«зум» после сохранения).
        setImgNatural({ w: 0, h: 0 });
        setSrcError(false);
        const img = new Image();
        img.src = editorSrc;
        const apply = () => {
            if (cancelled) return;
            // 0×0 = файл не декодировался (битый/0 байт/удалён) — это не фото
            if (!img.naturalWidth || !img.naturalHeight) {
                setSrcError(true);
                return;
            }
            setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
            requestAnimationFrame(() => {
                if (!cancelled) setLayoutTick(t => t + 1);
            });
        };
        img.onerror = () => { if (!cancelled) setSrcError(true); };
        if (img.decode) {
            img.decode().then(apply).catch(apply);   // catch: применим и если decode не поддержан/отклонён
        } else {
            img.onload = apply;
        }
        return () => { cancelled = true; };
    }, [editorSrc]);

    // ── Блокировка скролла ────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            document.body.style.overflow    = "hidden";
            document.body.style.touchAction = "none";
        } else {
            document.body.style.overflow    = "";
            document.body.style.touchAction = "";
        }
        return () => {
            document.body.style.overflow    = "";
            document.body.style.touchAction = "";
        };
    }, [open]);

    // ── Вычисление зон обрезки в px ──────────────────────────────────────────
    const computeBleed = useCallback(() => {
        const el = frameRef.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();

        // ── Красный корешок: только если слот реально касается корешка
        //    (side приходит null, если не касается — см. PhotoBookEditor).
        //    Доля приходит готовой из spine.config.getSpineFraction():
        //    соло → 8% (база), маленькая ячейка big-left → ≈19.2%, grid-4 → 16%.
        const spinePx = Math.round(width * (spineFraction ?? SPINE_PERCENT));
        setSpine({
            top:    0,
            bottom: 0,
            left:   side === "right" ? spinePx : 0,
            right:  side === "left"  ? spinePx : 0,
        });

        // ── Желтый bleed (вылет под обрез): задаётся долями от СТРАНИЦЫ
        //    (spine.config.js) и приходит сюда пересчитанным в долю ЭТОЙ ячейки —
        //    редактор обещает ровно то, что конструктор скрывает.
        const bleedXpx = Math.round(width  * (bleedFractionX ?? BLEED_PERCENT));
        const bleedYpx = Math.round(height * (bleedFractionY ?? BLEED_PERCENT));

        const edges = slotEdges ?? { top: true, right: true, bottom: true, left: true };
        setYellow({
            top:    edges.top    ? bleedYpx : 0,
            // со стороны корешка желтого вылета нет — там корешок
            bottom: edges.bottom ? bleedYpx : 0,
            left:   (edges.left  && side !== "right") ? bleedXpx : 0,
            right:  (edges.right && side !== "left")  ? bleedXpx : 0,
        });
    }, [side, slotEdges, bookSize, slotAspectRatio, spineFraction, bleedFractionX, bleedFractionY]);

    useEffect(() => {
        if (!open) return;
        const t = setTimeout(computeBleed, 80);
        return () => clearTimeout(t);
    }, [open, computeBleed]);

    // ── Вычисляем размер фото внутри рамки (cover) ──────────────────────────
    const getRenderedImgSize = useCallback(() => {
        const frame = frameRef.current;
        if (!frame || !imgNatural.w || !imgNatural.h) return { w: 100, h: 100 };
        const { width: fw, height: fh } = frame.getBoundingClientRect();

        const norm = ((rotation % 360) + 360) % 360;
        const isRotated90 = norm === 90 || norm === 270;
        const natW = isRotated90 ? imgNatural.h : imgNatural.w;
        const natH = isRotated90 ? imgNatural.w : imgNatural.h;

        const imgAr   = natW / natH;
        const frameAr = fw / fh;
        let baseW, baseH;
        if (imgAr > frameAr) {
            baseH = fh;
            baseW = fh * imgAr;
        } else {
            baseW = fw;
            baseH = fw / imgAr;
        }
        // ВАЖНО: возвращаем размеры ДО поворота элемента <img>,
        // т.к. CSS rotate применяется поверх width/height.
        // При повороте 90° меняем местами обратно, чтобы после rotate(90deg)
        // bbox совпал с baseW × baseH.
        if (isRotated90) {
            return { w: baseH * scale, h: baseW * scale };
        }
        return { w: baseW * scale, h: baseH * scale };
    }, [imgNatural, scale, rotation]);

    // ── Snap-back ──────────────────────────────────────────────────────────────
    const snapBack = useCallback(() => {
        setIsDragging(false);
        const frame = frameRef.current;
        if (!frame || !imgNatural.w) return;
        const { width: fw, height: fh } = frame.getBoundingClientRect();
        // Пропорция С УЧЁТОМ поворота: после 90°/270° ширина/высота меняются местами.
        const isRot90 = Math.abs(rotation % 180) === 90;
        const natW = isRot90 ? imgNatural.h : imgNatural.w;
        const natH = isRot90 ? imgNatural.w : imgNatural.h;
        const imgAr   = natW / natH;
        const frameAr = fw / fh;

        setScale(prevScale => {
            const nextScale = Math.max(prevScale, minScaleRef.current);

            let baseW, baseH;
            if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
            else                  { baseW = fw; baseH = fw / imgAr; }
            const rw = baseW * nextScale;
            const rh = baseH * nextScale;
            const maxX = Math.max(0, (rw - fw) / 2);
            const maxY = Math.max(0, (rh - fh) / 2);

            setOffset(prev => ({
                x: clamp(prev.x, -maxX, maxX),
                y: clamp(prev.y, -maxY, maxY),
            }));
            return nextScale;
        });
    }, [imgNatural, rotation]);

    // ── Touch ──────────────────────────────────────────────────────────────────
    const handleTouchStart = useCallback((e) => {
        if (!readyRef.current) return;
        setIsDragging(true);
        if (e.touches.length === 1) {
            dragStart.current   = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            offsetStart.current = { x: offset.x, y: offset.y };
        }
        if (e.touches.length === 2) {
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            pinchStart.current = { dist: Math.hypot(dx, dy), scale };
        }
    }, [offset, scale]);

    const handleTouchMove = useCallback((e) => {
        e.preventDefault();
        if (e.touches.length === 1 && dragStart.current) {
            const dx = e.touches[0].clientX - dragStart.current.x;
            const dy = e.touches[0].clientY - dragStart.current.y;
            setOffset({ x: offsetStart.current.x + dx, y: offsetStart.current.y + dy });
        }
        if (e.touches.length === 2 && pinchStart.current) {
            const dx   = e.touches[0].clientX - e.touches[1].clientX;
            const dy   = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.hypot(dx, dy);
            setScale(clamp(pinchStart.current.scale * (dist / pinchStart.current.dist), minScaleRef.current, 8));
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        dragStart.current  = null;
        pinchStart.current = null;
        snapBack();
    }, [snapBack]);

    // ── Mouse drag ─────────────────────────────────────────────────────────────
    const handleMouseDown = useCallback((e) => {
        if (!readyRef.current) return;   // до готовности двигать нельзя
        e.preventDefault();
        setIsDragging(true);
        dragStart.current   = { x: e.clientX, y: e.clientY };
        offsetStart.current = { x: offset.x, y: offset.y };

        const onMove = (ev) => {
            setOffset({
                x: offsetStart.current.x + ev.clientX - dragStart.current.x,
                y: offsetStart.current.y + ev.clientY - dragStart.current.y,
            });
        };
        const onUp = () => {
            dragStart.current = null;
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup",   onUp);
            snapBack();
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup",   onUp);
    }, [offset, snapBack]);

    // ── Минимальный масштаб = «фото полностью покрывает рамку» (защита от чёрных краёв) ──
    // Ниже этого зумить нельзя: при scale < min по краям вылезет фон слота.
    const minScaleRef = useRef(1);
    useEffect(() => {
        const frame = frameRef.current;
        if (!frame || !imgNatural.w || !imgNatural.h) { minScaleRef.current = 1; setMinZoom(1); return; }
        const { width: fw, height: fh } = frame.getBoundingClientRect();
        const isRot90 = Math.abs(rotation % 180) === 90;
        const natW = isRot90 ? imgNatural.h : imgNatural.w;
        const natH = isRot90 ? imgNatural.w : imgNatural.h;
        const imgAr = natW / natH, frameAr = fw / fh;
        let baseW, baseH;
        if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
        else                 { baseW = fw; baseH = fw / imgAr; }
        const m = Math.max(1, fw / baseW, fh / baseH) * COVER_EPS;
        minScaleRef.current = m;
        setMinZoom(m);
        // Подтягиваем текущий масштаб к покрывающему минимуму — на случай, если он ниже
        // (тяжёлое фото догрузилось позже; сохранённый scale чуть ниже cover).
        // scale НЕ в зависимостях эффекта — цикла не будет.
        setScale(s => {
            const next = s < m ? m : s;
            // Смещение зажимаем ТЕМ ЖЕ клэмпом, что в snapBack/applyZoom: при
            // смене размеров рамки (resize окна, поворот экрана, догрузка фото)
            // старый offset мог оставить рамку частично НЕ покрытой фото — из
            // этого состояния и уходили «вылезшие» координаты кропа.
            const rw = baseW * next;
            const rh = baseH * next;
            const maxX = Math.max(0, (rw - fw) / 2);
            const maxY = Math.max(0, (rh - fh) / 2);
            setOffset(o => ({
                x: clamp(o.x, -maxX, maxX),
                y: clamp(o.y, -maxY, maxY),
            }));
            return next;
        });
    }, [imgNatural, rotation, containerSize, open, layoutTick]);

    // ── Единая точка зума (слайдер / кнопки) ────────────────────────────────────
    // Тот же клэмп, что в snapBack: НЕ ниже cover-минимума (пружина) и НЕ выше
    // ZOOM_MAX; смещение зажимаем так, чтобы стороны не вылезли за фото. База
    // cover считается С УЧЁТОМ поворота (после 90°/270° стороны меняются местами).
    const applyZoom = useCallback((target) => {
        if (!readyRef.current) return;
        const frame = frameRef.current;
        if (!frame || !imgNatural.w || !imgNatural.h) return;
        const { width: fw, height: fh } = frame.getBoundingClientRect();

        const isRot90 = Math.abs(rotation % 180) === 90;
        const natW = isRot90 ? imgNatural.h : imgNatural.w;
        const natH = isRot90 ? imgNatural.w : imgNatural.h;
        const imgAr = natW / natH;
        const frameAr = fw / fh;
        let baseW, baseH;
        if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
        else                 { baseW = fw; baseH = fw / imgAr; }

        const next = clamp(target, minScaleRef.current, ZOOM_MAX);
        const rw = baseW * next;
        const rh = baseH * next;
        const maxX = Math.max(0, (rw - fw) / 2);
        const maxY = Math.max(0, (rh - fh) / 2);

        setScale(next);
        setOffset(o => ({
            x: clamp(o.x, -maxX, maxX),
            y: clamp(o.y, -maxY, maxY),
        }));
    }, [imgNatural, rotation]);

    // ── Колёсико мыши — зум ────────────────────────────────────────────────────
    useEffect(() => {
        const el = frameRef.current;
        if (!el || !open) return;
        const handleWheel = (e) => {
            e.preventDefault();
            const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
            setScale(prev => {
                const next = clamp(prev * factor, minScaleRef.current, 8);
                const frame = frameRef.current;
                if (!frame || !imgNatural.w) return next;
                const { width: fw, height: fh } = frame.getBoundingClientRect();
                // Пропорция С УЧЁТОМ поворота (после 90°/270° стороны меняются местами).
                const isRot90 = Math.abs(rotation % 180) === 90;
                const natW = isRot90 ? imgNatural.h : imgNatural.w;
                const natH = isRot90 ? imgNatural.w : imgNatural.h;
                const imgAr = natW / natH;
                const frameAr = fw / fh;
                let baseW, baseH;
                if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
                else                  { baseW = fw; baseH = fw / imgAr; }
                const rw = baseW * next;
                const rh = baseH * next;
                const maxX = Math.max(0, (rw - fw) / 2);
                const maxY = Math.max(0, (rh - fh) / 2);
                setOffset(o => ({
                    x: clamp(o.x, -maxX, maxX),
                    y: clamp(o.y, -maxY, maxY),
                }));
                return next;
            });
        };
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [open, imgNatural, rotation]);

    // ── Поворот ────────────────────────────────────────────────────────────────
    const calcMinScaleAfterRotation = (newRotation) => {
        const frame = frameRef.current;
        if (!frame || !imgNatural.w || !imgNatural.h) return 1;
        const { width: fw, height: fh } = frame.getBoundingClientRect();

        // После поворота на 90°/270° натуральные размеры меняются местами
        const isRotated90 = Math.abs(newRotation % 180) === 90;
        const natW = isRotated90 ? imgNatural.h : imgNatural.w;
        const natH = isRotated90 ? imgNatural.w : imgNatural.h;
        const imgAr = natW / natH;
        const frameAr = fw / fh;

        // Базовый размер фото в рамке (fit: cover)
        let baseW, baseH;
        if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
        else                  { baseW = fw; baseH = fw / imgAr; }

        // Минимальный scale чтобы повёрнутое фото полностью покрывало рамку
        const minScaleW = fw / baseW;
        const minScaleH = fh / baseH;
        return Math.max(1, minScaleW, minScaleH) * COVER_EPS;
    };

    // Логируем поворот фото сразу (для разбора жалоб «фото повёрнуто»)
    const logRotation = (fromDeg, toDeg, direction) => {
        try {
            const ctx = logContext || {};
            const norm = (d) => (((d % 360) + 360) % 360);
            const from = norm(fromDeg);
            const to = norm(toDeg);
            const sideRu = ctx.side === "left" ? "левая страница"
                         : ctx.side === "right" ? "правая страница" : "";
            const where = ctx.location === "cover"
                ? `обложка, ячейка ${ctx.gridIndex ?? "—"}`
                : [
                    `слот ${ctx.slotIndex ?? "—"}`,
                    sideRu,
                    ctx.pageIndex != null ? `разворот #${ctx.pageIndex}` : "",
                  ].filter(Boolean).join(", ");
            logEvent({
                level: "info",
                source: "ui",
                event: "photo_rotate",
                book_id: ctx.bookId || null,
                message: `Поворот фото: ${where} — ${from}° → ${to}°`,
                payload: {
                    action: "rotate",
                    direction,                 // "cw" | "ccw"
                    from_deg: from,
                    to_deg: to,
                    photo_id: ctx.photoId || null,
                    location: ctx.location || "page",
                    page_index: ctx.pageIndex ?? null,
                    side: ctx.side || null,
                    slot_index: ctx.slotIndex ?? null,
                    grid_index: ctx.gridIndex ?? null,
                },
            });
        } catch (e) {
            /* лог не должен ломать редактор */
        }
    };

    // Старт масштаба в текущем жесте слайдера (для from → to). null = жеста нет.
    const zoomFromRef = useRef(null);

    // Лог зума — тот же формат, что logRotation. Вызываем на ЗАВЕРШЕНИИ действия
    // (клик кнопки / отпускание слайдера), а не на каждый промежуточный кадр.
    const logZoom = (fromScale, toScale, source) => {
        try {
            const ctx = logContext || {};
            const r = (v) => Math.round(v * 1000) / 1000;
            const from = r(fromScale);
            const to = r(toScale);
            const sideRu = ctx.side === "left" ? "левая страница"
                         : ctx.side === "right" ? "правая страница" : "";
            const where = ctx.location === "cover"
                ? `обложка, ячейка ${ctx.gridIndex ?? "—"}`
                : [
                    `слот ${ctx.slotIndex ?? "—"}`,
                    sideRu,
                    ctx.pageIndex != null ? `разворот #${ctx.pageIndex}` : "",
                  ].filter(Boolean).join(", ");
            logEvent({
                level: "info",
                source: "ui",
                event: "photo_zoom",
                book_id: ctx.bookId || null,
                message: `Зум фото: ${where} — ${from}x → ${to}x (${source})`,
                payload: {
                    action: "zoom",
                    source,                    // "кнопка +" | "кнопка −" | "слайдер"
                    from_scale: from,
                    to_scale: to,
                    rotation,                  // текущий угол — полезно при разборе
                    photo_id: ctx.photoId || null,
                    location: ctx.location || "page",
                    page_index: ctx.pageIndex ?? null,
                    side: ctx.side || null,
                    slot_index: ctx.slotIndex ?? null,
                    grid_index: ctx.gridIndex ?? null,
                },
            });
        } catch (e) {
            /* лог не должен ломать редактор */
        }
    };

    const rotateLeft = () => {
        if (!readyRef.current) return;
        const from = rotation;
        const to = from - 90;
        setRotation(to);
        setOffset({ x: 0, y: 0 });
        setScale(calcMinScaleAfterRotation(to));
        logRotation(from, to, "ccw");
    };

    const rotateRight = () => {
        if (!readyRef.current) return;
        const from = rotation;
        const to = from + 90;
        setRotation(to);
        setOffset({ x: 0, y: 0 });
        setScale(calcMinScaleAfterRotation(to));
        logRotation(from, to, "cw");
    };

    // ══════════════════════════════════════════════════════════════════════════
    // ── Подтверждение: кропим через canvas + сохраняем оригинал и параметры ──
    // ══════════════════════════════════════════════════════════════════════════
    const handleConfirm = useCallback(async () => {
        if (busy) return;
        // Хеш ИСХОДНОГО фото (до кропа) — по нему сервер ловит дубли независимо от зума/кропа.
        const sourceHash = photo?.file ? await sha256Hex(photo.file) : "";
        const frame = frameRef.current;
        if (!frame || !imgNatural.w) {
            // Оригинал ещё не декодирован (тяжёлое фото / медленная сеть) —
            // координаты выреза считать не из чего. Раньше здесь молча уходил
            // объект со СТАРЫМ photo.cropData: сервер пере-резал фото по старым
            // координатам, и вместо нового кадра сохранялся прежний (тесный) —
            // выглядело как «внезапный зум х2 после сохранения».
            alert("Фото ещё загружается. Подождите секунду и нажмите сохранить ещё раз.");
            return;
        }
        const { width: fw, height: fh } = frame.getBoundingClientRect();

        // Нормализованный угол 0..359
        const norm = ((rotation % 360) + 360) % 360;
        const isRotated90 = norm === 90 || norm === 270;

        // Натуральные размеры с учётом поворота (то, что реально видно на экране как bbox)
        const natW = isRotated90 ? imgNatural.h : imgNatural.w;
        const natH = isRotated90 ? imgNatural.w : imgNatural.h;

        // Базовый cover-размер на экране (для ПОВЁРНУТОГО изображения)
        const imgAr   = natW / natH;
        const frameAr = fw / fh;
        let baseW, baseH;
        if (imgAr > frameAr) { baseH = fh; baseW = fh * imgAr; }
        else                 { baseW = fw; baseH = fw / imgAr; }

        // ГАРАНТИЯ COVER ПРИ БЕЙКЕ. Какими бы ни пришли scale/offset (баги клэмпа
        // при повороте/зуме/восстановлении), пересчитываем их так, чтобы рамка
        // была ПОЛНОСТЬЮ покрыта фото. Иначе на canvas останется непокрытая зона,
        // а JPEG зальёт её чёрным → полоса в книге.
        const coverMinScale = Math.max(1, fw / baseW, fh / baseH) * COVER_EPS;
        const effScale = Math.max(scale, coverMinScale);
        const renderedW = baseW * effScale;
        const renderedH = baseH * effScale;

        // Смещение зажимаем в зону полного покрытия рамки
        const maxOffX = Math.max(0, (renderedW - fw) / 2);
        const maxOffY = Math.max(0, (renderedH - fh) / 2);
        const effOffX = clamp(offset.x, -maxOffX, maxOffX);
        const effOffY = clamp(offset.y, -maxOffY, maxOffY);

        // Нормализованный offset для сохранения (по эффективным значениям)
        const normOffsetX = renderedW > 0 ? effOffX / renderedW : 0;
        const normOffsetY = renderedH > 0 ? effOffY / renderedH : 0;

        // Область кропа в пикселях рамки
        const cropX = 0;
        const cropY = 0;
        const cropW = fw;
        const cropH = fh;

        // Положение (повёрнутого) фото в рамке — по эффективным scale/offset
        const imgLeft = (fw - renderedW) / 2 + effOffX;
        const imgTop  = (fh - renderedH) / 2 + effOffY;

        // px рамки → px НАТУРАЛЬНОГО (повёрнутого) изображения
        const scaleToNat = natW / renderedW;

        const srcX = Math.round((cropX - imgLeft) * scaleToNat);
        const srcY = Math.round((cropY - imgTop)  * scaleToNat);
        const srcW = Math.round(cropW * scaleToNat);
        const srcH = Math.round(cropH * scaleToNat);

        // ── НОВАЯ ВЕРСИЯ: НЕ пекём канвас. Отдаём ОРИГИНАЛ + координаты выреза.
        //    src — доли [0..1] от ПОВЁРНУТОГО оригинала (natW×natH). Сервер режет
        //    движком: выйти за пределы фото нельзя → чёрных/белых полос быть не может.
        // Пропорцию выреза сохраняем ВСЕГДА (зеркало серверной нормализации).
        // Покомпонентный clamp мог отдать x+w>1 / y+h>1 (округление, непокрытая
        // рамка) — старый сервер «чинил» это независимым зажимом осей и ломал
        // пропорцию → фото растягивалось в слоте и в печати.
        let sx = srcX / natW;
        let sy = srcY / natH;
        let sw = srcW / natW;
        let sh = srcH / natH;
        if (sw <= 0) sw = 1e-4;
        if (sh <= 0) sh = 1e-4;
        // 1) Вырез больше фото → ужимаем ОДНИМ коэффициентом вокруг центра
        //    (пропорция не меняется).
        const fitK = Math.min(1, 1 / sw, 1 / sh);
        if (fitK < 1) {
            const cx = sx + sw / 2, cy = sy + sh / 2;
            sw *= fitK; sh *= fitK;
            sx = cx - sw / 2; sy = cy - sh / 2;
        }
        // 2) Сдвигаем вырез внутрь фото (меняется позиция, не пропорция).
        sx = clamp(sx, 0, 1 - sw);
        sy = clamp(sy, 0, 1 - sh);
        const src = { x: sx, y: sy, w: sw, h: sh };
        const cropData = { v: 2, rotation: norm, src };

        // Диагностика (только лог, синхронно, без чтения файла): во сколько раз
        // вырез растягивается до печатного слота. >1 → апскейл (риск мыла/полос).
        let _diag = {};
        try {
            const cropPxW = Math.round(src.w * imgNatural.w);
            const cropPxH = Math.round(src.h * imgNatural.h);
            const slotW = Math.round(155 / 25.4 * 300);
            const arr = cropPxH > 0 ? cropPxW / cropPxH : 1;
            const slotH = Math.round(slotW / (arr || 1));
            const ux = +(slotW / Math.max(1, cropPxW)).toFixed(2);
            const uy = +(slotH / Math.max(1, cropPxH)).toFixed(2);
            _diag = {
                crop_px_w: cropPxW, crop_px_h: cropPxH,
                upscale: Math.max(ux, uy), will_upscale: Math.max(ux, uy) > 1.05,
            };
        } catch (e) { _diag = {}; }

        // Диагностика жалоб «кадр не тот» + апскейл (риск мыла/полос).
        try {
            const ctx = logContext || {};
            logEvent({
                level: _diag.will_upscale ? "warn" : "info",
                source: "ui", event: "photo_crop_confirm",
                book_id: ctx.bookId || null,
                message: `Кроп: src=(${src.x.toFixed(3)},${src.y.toFixed(3)},${src.w.toFixed(3)},${src.h.toFixed(3)}) rot=${norm} ориг ${imgNatural.w}x${imgNatural.h} вырез ${_diag.crop_px_w ?? "?"}x${_diag.crop_px_h ?? "?"} апскейл x${_diag.upscale ?? "?"}${_diag.will_upscale ? " ⚠ФОТО МЕЛКОЕ" : ""} scale=${scale.toFixed(3)}`,
                payload: {
                    src, rotation: norm,
                    natural_w: imgNatural.w, natural_h: imgNatural.h,
                    ..._diag,
                    scale, offset_x: offset.x, offset_y: offset.y,
                    photo_id: ctx.photoId || null, location: ctx.location || "page",
                    page_index: ctx.pageIndex ?? null, side: ctx.side || null,
                    slot_index: ctx.slotIndex ?? null, grid_index: ctx.gridIndex ?? null,
                },
            });
        } catch (e) { /* лог не должен ломать сохранение */ }

        onConfirm({
            ...photo,
            originalFile: photo.file || null,   // ОРИГИНАЛ (не обрезанный); null при пере-сохранении серверного фото
            cropData,                            // координаты выреза
            url: photo.originalUrl || photo.url, // оптимистичное превью (оригинал), заменится серверным
            originalUrl: photo.originalUrl || photo.url,
            rotation,
            scale: effScale,
            offsetX: normOffsetX,
            offsetY: normOffsetY,
            isCropped: true,
            converted: didConvertRef.current,
            sourceHash,
        });
    }, [photo, editorSrc, rotation, scale, offset, imgNatural, onConfirm, busy]);

    // ── Замена файла ───────────────────────────────────────────────────────────
    const handleReplaceChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        onReplace(file);
        e.target.value = "";
    };

    // ── Размеры рамки ─────────────────────────────────────────────────────────
    // widthMm — ширина ОДНОЙ страницы, делить на 2 не нужно
    // COVER_FRAME_HEIGHT_SCALE < 1 делает рамку НИЖЕ (шире по пропорции).
    // ВАЖНО: применяется ТОЛЬКО к фото задней ОБЛОЖКИ (isCoverBack) — там ячейка
    // на печати визуально ниже рамки. Для СТРАНИЦ книги рамка обязана иметь
    // честную пропорцию ячейки, иначе кроп получается шире ячейки и фото
    // в конструкторе/печати растягивается по высоте.
    const COVER_FRAME_HEIGHT_SCALE = 0.90;
    const baseRatio = slotAspectRatio ?? (bookSize
        ? bookSize.widthMm / bookSize.heightMm
        : 0.75);
    // Новые фиксированные рамки кадрируются строго в пропорции PDF.
    // Legacy одиночного фото сохраняет прежнее поведение.
    const fixedCoverFrame = exactCoverAspect || coverShape === "square" || coverShape === "rectangle";
    const pageRatio = isCoverBack && !fixedCoverFrame
        ? baseRatio / COVER_FRAME_HEIGHT_SCALE
        : baseRatio;

    // По 64 px с каждой стороны: справа панель зума, слева такой же
    // резерв, чтобы рамка оставалась в центре и не попадала под панель.
    const availW = Math.max(1, containerSize.w > 0 ? containerSize.w - 128 : 272);
    const availH = Math.max(1, containerSize.h > 0 ? containerSize.h - 16 : 400);

    const maxFrameW = availW;
    const maxFrameH = availH;

    let frameW, frameH;
    if (maxFrameW / maxFrameH > pageRatio) {
        frameH = maxFrameH;
        frameW = Math.round(maxFrameH * pageRatio);
    } else {
        frameW = maxFrameW;
        frameH = Math.round(maxFrameW / pageRatio);
    }

    // ── Размер фото для рендера ───────────────────────────────────────────────
    const rendered = getRenderedImgSize();


    if (!photo) return null;

    return (
        <AnimatePresence>
            {open && (
                <Modal
                    key="photo-editor-modal"
                    sx={{ zIndex: 10013 }}
                    open={open}
                    slotProps={{
                        backdrop: {
                            component: motion.div,
                            initial: { opacity: 0, backdropFilter: 'blur(0px)' },
                            animate: { opacity: 1, backdropFilter: 'blur(8px)' },
                            exit: { opacity: 0, backdropFilter: 'blur(0px)' },
                            transition: { duration: 0.3, ease: "easeInOut" },
                        }
                    }}
                >
                    <ModalOverflow
                        sx={(theme) => ({
                            p: 0,
                            '--ModalOverflow-paddingY': '0px',
                            // ModalOverflow задаёт layoutCenter высоту max-content.
                            // Переопределяем её здесь, тем же вложенным селектором.
                            '& .MuiModalDialog-layoutCenter': {
                                boxSizing: 'border-box',
                                height: 'min(760px, calc(100dvh - 32px))',
                                maxHeight: 'calc(100dvh - 32px)',
                                minHeight: 0,
                                flexShrink: 0,
                                [theme.breakpoints.down('sm')]: {
                                    height: '100dvh',
                                    maxHeight: '100dvh',
                                },
                            },
                        })}
                    >
                        <ModalDialog
                            size="lg"
                            component={motion.div}
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.98 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                            sx={(theme) => ({
                                display: 'flex',
                                flexDirection: 'column',
                                boxSizing: 'border-box',
                                height: 'min(760px, calc(100dvh - 32px))',
                                maxHeight: 'calc(100dvh - 32px)',
                                minHeight: 0,
                                overflowY: 'auto',
                                [theme.breakpoints.up("xl")]: {
                                    width: '800px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("xl")]: {
                                    width: '700px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("lg")]: {
                                    width: '600px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("md")]: {
                                    width: '500px',
                                    mx: 0,
                                },
                                [theme.breakpoints.down("sm")]: {
                                    width: '100vw',
                                    minWidth: '100vw',
                                    minHeight: 0,
                                    height: '100dvh',
                                    maxHeight: '100dvh',
                                    py: 1,
                                    px: 0,
                                    m: 0, 
                                    borderRadius: 0
                                },
                                p: 3,
                                width: '100%'
                            })}
                        >
                            {/* ── Шапка ── */}
                            <Box sx={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                px: 2, py: 1.5, flexShrink: 0,
                            }}>
                                <IconButton onClick={onClose} variant="soft" color="danger">
                                    <CloseRoundedIcon />
                                </IconButton>
                                <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
                                    Редактор
                                </Typography>
                                <IconButton onClick={handleConfirm} variant="soft" color="success" disabled={busy || preparing || !ready}>
                                    <CheckRoundedIcon />
                                </IconButton>
                            </Box>

                            {busy && (
                              <Box sx={{ position: "absolute", inset: 0, zIndex: 100,
                                bgcolor: "rgba(255,255,255,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                Сохраняем фото…
                              </Box>
                            )}

                            {/* ── Галочка «сжимать фото» (только по умной ссылке) ── */}
                            {fitMode && (
                              <Box sx={{ px: 2, pb: 0.5, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                <Box component="label" sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    fontSize: 14, color: "neutral.400", cursor: "pointer",
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={compress}
                                        onChange={(e) => { setCompress(e.target.checked); setCompressOn(e.target.checked); }}
                                    />
                                    Сжимать фото {preparing ? "(обработка…)" : ""}
                                </Box>
                              </Box>
                            )}

                            {/* ── Галочка «конвертировать HEIC→JPG» (только по умной ссылке) ── */}
                            {convertMode && (
                              <Box sx={{ px: 2, pb: 0.5, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                <Box component="label" sx={{
                                    display: "flex", alignItems: "center", gap: 1,
                                    fontSize: 14, color: "neutral.400", cursor: "pointer",
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={convert}
                                        onChange={(e) => { setConvert(e.target.checked); setConvertOn(e.target.checked); }}
                                    />
                                    Конвертировать HEIC→JPG {preparing ? "(обработка…)" : ""}
                                </Box>
                              </Box>
                            )}

                            {/* ── Область превью ── */}
                            <Box
                                ref={containerRef}
                                sx={{
                                    flex: '1 1 0px', minHeight: 180,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    px: 2, py: 1,
                                    overflow: "hidden",
                                    position: "relative",
                                }}
                            >
                                {/* ── Зум: кнопки + вертикальный слайдер (справа) ── */}
                                {imgNatural.w > 0 && (
                                    <Box sx={{
                                        position: "absolute", right: 12, top: "50%",
                                        transform: "translateY(-50%)", zIndex: 15,
                                        display: "flex", flexDirection: "column",
                                        alignItems: "center", gap: 1,
                                        bgcolor: "rgba(255,255,255,0.92)",
                                        borderRadius: "16px", py: 1.25, px: 0.75,
                                        boxShadow: "0 2px 12px rgba(0,0,0,0.14)",
                                    }}>
                                        <IconButton
                                            size="sm" variant="soft" color="neutral"
                                            aria-label="Приблизить"
                                            onClick={() => {
                                                const to = clamp(scale * ZOOM_STEP, minScaleRef.current, ZOOM_MAX);
                                                if (Math.abs(to - scale) > 1e-3) { applyZoom(to); logZoom(scale, to, "кнопка +"); }
                                            }}
                                        >
                                            <AddRounded />
                                        </IconButton>
                                        <Slider
                                            orientation="vertical"
                                            size="sm"
                                            min={minZoom}
                                            max={ZOOM_MAX}
                                            step={0.01}
                                            value={scale}
                                            onChange={(_, v) => {
                                                if (zoomFromRef.current == null) zoomFromRef.current = scale; // запомнили старт жеста
                                                applyZoom(Array.isArray(v) ? v[0] : v);
                                            }}
                                            onChangeCommitted={(_, v) => {
                                                const raw = Array.isArray(v) ? v[0] : v;
                                                const to = clamp(raw, minScaleRef.current, ZOOM_MAX);
                                                const from = zoomFromRef.current == null ? scale : zoomFromRef.current;
                                                if (Math.abs(to - from) > 1e-3) logZoom(from, to, "слайдер");
                                                zoomFromRef.current = null;
                                            }}
                                            sx={{ height: 150 }}
                                        />
                                        <IconButton
                                            size="sm" variant="soft" color="neutral"
                                            aria-label="Уменьшить"
                                            onClick={() => {
                                                const to = clamp(scale / ZOOM_STEP, minScaleRef.current, ZOOM_MAX);
                                                if (Math.abs(to - scale) > 1e-3) { applyZoom(to); logZoom(scale, to, "кнопка −"); }
                                            }}
                                        >
                                            <RemoveRounded />
                                        </IconButton>
                                    </Box>
                                )}

                                <Box
                                    ref={frameRef}
                                    onMouseDown={handleMouseDown}
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                    sx={{
                                        position: "relative",
                                        width: `${frameW}px`,
                                        height: `${frameH}px`,
                                        maxWidth: "100%",
                                        maxHeight: "100%",
                                        overflow: "hidden",
                                        borderRadius: "3px",
                                        bgcolor: previewBackground,
                                        cursor: isDragging ? "grabbing" : "grab",
                                        userSelect: "none",
                                        touchAction: "none",
                                    }}
                                >
                                    {/* Оригинал недоступен — понятное сообщение вместо битой иконки */}
                                    {srcError && (
                                        <Box
                                            sx={{
                                                position: "absolute", inset: 0, zIndex: 6,
                                                display: "flex", flexDirection: "column", gap: 1,
                                                alignItems: "center", justifyContent: "center",
                                                textAlign: "center", p: 2,
                                                bgcolor: "rgba(20,20,24,0.92)",
                                            }}
                                        >
                                            <Box sx={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>
                                                Оригинал фото недоступен
                                            </Box>
                                            <Box sx={{ fontSize: 13, lineHeight: 1.35, maxWidth: 320, color: "rgba(255,255,255,0.75)" }}>
                                                Файл не открывается, кадрировать это фото нельзя.
                                                Нажмите «Заменить фото» и загрузите снимок заново.
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Фото — всегда ОРИГИНАЛ */}
                                    <Box
                                        component="img"
                                        src={editorSrc}
                                        draggable={false}
                                        sx={{
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            width:  `${rendered.w}px`,
                                            height: `${rendered.h}px`,
                                            transform: `
                                                translate(-50%, -50%)
                                                translate(${offset.x}px, ${offset.y}px)
                                                rotate(${rotation}deg)
                                            `,
                                            transformOrigin: "center center",
                                            transition: isDragging
                                                ? "none"
                                                : "transform 0.32s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                                            pointerEvents: "none",
                                            willChange: "transform",
                                            display: "block",
                                        }}
                                    />

                                    {(preparing || !ready) && (
                                        <Box sx={{
                                            position: "absolute", inset: 0, zIndex: 20,
                                            display: "flex", flexDirection: "column", gap: 1.25,
                                            alignItems: "center", justifyContent: "center",
                                            backdropFilter: "blur(10px)",
                                            background: "rgba(20,20,24,0.35)",
                                            color: "rgba(255,255,255,0.9)", fontSize: 14,
                                            transition: "opacity 0.25s",
                                        }}>
                                            <Box sx={{
                                                width: 30, height: 30,
                                                border: "3px solid rgba(255,255,255,0.35)",
                                                borderTopColor: "rgba(255,255,255,0.95)",
                                                borderRadius: "50%",
                                                animation: "mbspin 0.8s linear infinite",
                                                "@keyframes mbspin": {
                                                    "0%": { transform: "rotate(0deg)" },
                                                    "100%": { transform: "rotate(360deg)" },
                                                },
                                            }} />
                                            {preparing ? "Обработка фото…" : "Загрузка…"}
                                        </Box>
                                    )}

                                    {/* ── Оверлей: красный корешок + желтый bleed ── */}
                                    {!isCoverBack && (
                                        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10 }}>

                                            {/* ─ Желтые полоски bleed (4 мм, не обрезаются) ─ */}
                                            {yellow.top > 0 && (
                                                <Box sx={{
                                                    position: "absolute", top: 0, left: 0, right: 0,
                                                    height: `${yellow.top}px`,
                                                    bgcolor: "rgba(255, 200, 0, 0.55)",
                                                    borderBottom: "1px dashed rgba(220, 160, 0, 0.9)",
                                                }} />
                                            )}
                                            {yellow.bottom > 0 && (
                                                <Box sx={{
                                                    position: "absolute", bottom: 0, left: 0, right: 0,
                                                    height: `${yellow.bottom}px`,
                                                    bgcolor: "rgba(255, 200, 0, 0.55)",
                                                    borderTop: "1px dashed rgba(220, 160, 0, 0.9)",
                                                }} />
                                            )}
                                            {yellow.left > 0 && (
                                                <Box sx={{
                                                    position: "absolute", top: 0, bottom: 0, left: 0,
                                                    width: `${yellow.left}px`,
                                                    bgcolor: "rgba(255, 200, 0, 0.55)",
                                                    borderRight: "1px dashed rgba(220, 160, 0, 0.9)",
                                                }} />
                                            )}
                                            {yellow.right > 0 && (
                                                <Box sx={{
                                                    position: "absolute", top: 0, bottom: 0, right: 0,
                                                    width: `${yellow.right}px`,
                                                    bgcolor: "rgba(255, 200, 0, 0.55)",
                                                    borderLeft: "1px dashed rgba(220, 160, 0, 0.9)",
                                                }} />
                                            )}

                                            {/* ─ Красный корешок (обрезается при confirm) ─ */}
                                            {side === "left" && spine.right > 0 && (
                                                <>
                                                    <Box sx={{
                                                        position: "absolute", top: 0, bottom: 0, right: 0,
                                                        width: `${spine.right}px`,
                                                        bgcolor: "rgba(0, 0, 0, 0.50)",
                                                    }} />
                                                    <Box sx={{
                                                        position: "absolute", top: 0, bottom: 0,
                                                        right: `${spine.right}px`,
                                                        width: "1.5px",
                                                        bgcolor: "rgba(44, 44, 44, 1)",
                                                    }} />
                                                    <Box sx={{
                                                        position: "absolute", top: "50%",
                                                        right: Math.floor(spine.right / 2) - 5,
                                                        transform: "translateY(-50%)", writingMode: "vertical-rl",
                                                    }}>
                                                        <Typography sx={{ fontSize: 11, color: "rgba(255, 255, 255, 0.78)",
                                                            letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
                                                            корешок
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                            {side === "right" && spine.left > 0 && (
                                                <>
                                                    <Box sx={{
                                                        position: "absolute", top: 0, bottom: 0, left: 0,
                                                        width: `${spine.left}px`,
                                                        bgcolor: "rgba(0, 0, 0, 0.50)",
                                                    }} />
                                                    <Box sx={{
                                                        position: "absolute", top: 0, bottom: 0,
                                                        left: `${spine.left}px`,
                                                        width: "1.5px",
                                                        bgcolor: "rgba(44, 44, 44, 1)",
                                                    }} />
                                                    <Box sx={{
                                                        position: "absolute", top: "50%",
                                                        left: Math.floor(spine.left / 2) - 8,
                                                        transform: "translateY(-50%)", writingMode: "vertical-rl",
                                                    }}>
                                                        <Typography sx={{ fontSize: 11, color: "rgba(255, 255, 255, 0.78)",
                                                            letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
                                                            корешок
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* ── Тулбар ── */}
                            <Box sx={{
                                flexShrink: 0,
                                borderTop: "1px solid rgba(255,255,255,0.07)",
                                pb: "max(env(safe-area-inset-bottom), 8px)",
                            }}>
                                <Box sx={{
                                    display: "flex", justifyContent: "space-around", alignItems: "flex-start",
                                    px: 1, pt: 1, pb: 1.5,
                                }}>
                                    <ToolBtn icon={<RotateLeftRoundedIcon />}    label="Повернуть влево"  onClick={rotateLeft} />
                                    <ToolBtn icon={<RotateRightRoundedIcon />}   label="Повернуть вправо" onClick={rotateRight} />
                                    <ToolBtn icon={<ImageSearchRoundedIcon />}   label="Заменить фото"    onClick={() => replaceInputRef.current?.click()} />
                                    <ToolBtn icon={<DeleteOutlineRoundedIcon />} label="Удалить"          onClick={onDelete} danger />
                                </Box>
                            </Box>

                            <input ref={replaceInputRef} type="file" accept="image/*"
                                style={{ display: "none" }} onChange={handleReplaceChange} />
                        </ModalDialog>
                    </ModalOverflow>
                </Modal>
            )}
        </AnimatePresence>
    );
}